<template>
  <div :class="['login-container', { 'dark-theme': isDarkMode }]">
    <!-- Dark/Light Mode Toggle -->
    <div class="theme-toggle">
      <button @click="toggleTheme" class="theme-toggle-btn" :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'">
        <i :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
      </button>
    </div>

    <div class="d-flex flex-column min-vh-100">
      <!-- Header -->
      <header class="header-section border-bottom">
        <div class="container-fluid">
          <div class="d-flex align-items-center justify-content-between py-3">
            <div class="d-flex align-items-center justify-content-center">
              <img src="/logo_b.png" alt="SICECOM" class="login-logo" />
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="flex-grow-1 d-flex align-items-center justify-content-center py-5">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-12 col-md-6 col-lg-5 col-xl-4">
              <div class="login-card">
                <div class="text-center mb-4">
                  <h2 class="login-title">Inicio de sesión administrativo</h2>
                  <p class="login-subtitle">Accede al panel de administración</p>
                </div>

                <!-- Login Form -->
                <form @submit.prevent="onSubmit" class="login-form">
                  <div class="mb-3">
                    <label for="correo" class="form-label">Usuario o Correo</label>
                    <input
                      id="correo"
                      v-model.trim="correo"
                      type="email"
                      class="form-control login-input"
                      :class="{ 'is-invalid': error }"
                      placeholder="admin@empresa.com"
                      required
                      autocomplete="email"
                      :disabled="loading"
                    />
                  </div>

                  <div class="mb-4">
                    <label for="clave" class="form-label">Contraseña</label>
                    <div class="input-group">
                      <input
                        id="clave"
                        v-model="clave"
                        :type="showPassword ? 'text' : 'password'"
                        class="form-control login-input"
                        :class="{ 'is-invalid': error }"
                        placeholder="••••••••"
                        required
                        autocomplete="current-password"
                        :disabled="loading"
                      />
                      <button
                        type="button"
                        class="btn btn-outline-secondary password-toggle-btn"
                        @click="togglePasswordVisibility"
                        :disabled="loading"
                        :title="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                      >
                        <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Error Alert -->
                  <div v-if="error" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
                    <i class="bi bi-exclamation-triangle-fill me-2"></i>
                    <div>{{ error }}</div>
                  </div>

                  <!-- Login Button -->
                  <button
                    type="submit"
                    class="btn login-btn w-100 py-3"
                    :disabled="loading"
                  >
                    <div v-if="loading" class="d-flex align-items-center justify-content-center">
                      <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Cargando...</span>
                      </div>
                      Iniciando sesión...
                    </div>
                    <span v-else>Iniciar Sesión</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import auth from '@/services/servicioAutenticacion';
import store from '@/store/modules';
import themeManager from '@/store/theme';

export default {
  name: 'AdminLoginView',
  data() {
    return {
      correo: '',
      clave: '',
      loading: false,
      error: null,
      showPassword: false
    };
  },
  computed: {
    isDarkMode() {
      return themeManager.isDarkMode;
    }
  },
  mounted() {
    // Theme manager already initialized in main.js
    // Ensure theme is applied
    themeManager.applyTheme();
  },
  methods: {
    toggleTheme() {
      themeManager.toggleTheme();
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    async onSubmit() {
      this.error = null;
      this.loading = true;
      try {
        await auth.iniciarSesion(this.correo, this.clave);

        // Obtener datos del usuario y guardarlos en el store
        const perfil = await auth.obtenerPerfil();
        store.commit('setUser', perfil?.user ?? perfil);

        // Redirigir al destino solicitado o al dashboard
        const dest = this.$route.query?.redireccion || '/';
        this.$router.replace(dest);
      } catch (e) {
        // Mensajes claros por estado HTTP común
        const status = e?.response?.status;
        let msg = e?.response?.data?.error || e?.message || 'No se pudo iniciar sesión';
        if (status === 401) msg = 'Credenciales inválidas';
        if (status === 500) msg = 'Error del servidor. Intente nuevamente';
        this.error = msg;
      } finally {
        this.loading = false;
      }
    }
  },
  beforeUnmount() {
    // Limpiar clase del body al salir del componente
    document.body.classList.remove('dark-theme');
  }
};
</script>

<style>
/* Variables CSS para temas */
.login-container {
  --primary-color: #1173d4;
  --primary-hover: #0f5fa3;
  --bg-light: #f6f7f8;
  --bg-dark: #101922;
  --text-primary: #334155;
  --text-secondary: #64748b;
  --border-color: #e2e8f0;
  --card-bg: #ffffff;
  --card-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --header-border: #e2e8f0;
  
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  background: var(--bg-light);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Tema oscuro */
.login-container.dark-theme {
  --bg-light: #101922;
  --bg-dark: #0f172a;
  --text-primary: #f1f5f9;
  --text-secondary: #cbd5e1;
  --border-color: #334155;
  --card-bg: #1e293b;
  --card-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --header-border: #334155;
}

/* Toggle de tema */
.theme-toggle {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.theme-toggle-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: var(--card-bg);
  color: var(--text-primary);
  box-shadow: var(--card-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.theme-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Header */
.header-section {
  background: var(--card-bg);
  border-color: var(--header-border) !important;
  backdrop-filter: blur(10px);
}

.login-logo {
  width: 350px;
  height: 110px;
  object-fit: contain;
}



.logo-text {
  color: var(--text-primary) !important;
  font-weight: 900 !important;
  letter-spacing: -0.025em;
}

/* Contenedor principal */
.login-container {
  min-height: 100vh;
  background: var(--bg-light);
}

/* Degradado solo en tema oscuro */
.login-container.dark-theme {
  background: linear-gradient(135deg, var(--bg-light) 0%, var(--bg-dark) 100%);
}

/* Tarjeta de login */
.login-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: var(--card-shadow);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 32px 64px -12px rgba(0, 0, 0, 0.25);
}

/* Títulos */
.login-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.login-subtitle {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 0;
}

/* Formulario */
.login-form {
  margin-top: 2rem;
}

.login-input {
  background: var(--card-bg) !important;
  border: 2px solid var(--border-color) !important;
  color: var(--text-primary) !important;
  font-size: 0.875rem;
  padding: 0.875rem 1rem !important;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
}

.login-input:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 115, 212, 0.25) !important;
  background: var(--card-bg) !important;
}

.login-input::placeholder {
  color: var(--text-secondary) !important;
}

/* Labels tradicionales */
.form-label {
  color: var(--text-primary) !important;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  margin-bottom: 0.5rem !important;
  display: block !important;
}

/* Input group para contraseña */
.input-group {
  display: flex !important;
}

.input-group .login-input {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
}

.input-group .login-input:focus {
  border-right: none !important;
  z-index: 3 !important;
}

/* Botón de toggle de contraseña */
.password-toggle-btn {
  border: 2px solid var(--border-color) !important;
  border-left: none !important;
  background: var(--card-bg) !important;
  color: var(--text-secondary) !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
  padding: 0.875rem 1rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  transition: all 0.3s ease !important;
  font-size: 1rem !important;
  min-width: 48px !important;
}

.password-toggle-btn:hover:not(:disabled) {
  background: var(--bg-light) !important;
  color: var(--primary-color) !important;
  border-color: var(--border-color) !important;
}

.password-toggle-btn:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 0.2rem rgba(17, 115, 212, 0.25) !important;
  background: var(--card-bg) !important;
}

.password-toggle-btn:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Cuando el input tiene error, aplicar también al botón */
.input-group .login-input.is-invalid ~ .password-toggle-btn {
  border-color: #dc3545 !important;
}

/* Cuando el input está enfocado, sincronizar borde del botón */
.input-group .login-input:focus ~ .password-toggle-btn {
  border-color: var(--primary-color) !important;
}

/* Botón de login */
.login-btn {
  background: linear-gradient(135deg, var(--primary-color), #3b82f6) !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.025em !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(17, 115, 212, 0.39) !important;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 8px 25px 0 rgba(17, 115, 212, 0.5) !important;
  background: linear-gradient(135deg, var(--primary-hover), #2563eb) !important;
}

.login-btn:disabled {
  opacity: 0.7 !important;
  cursor: not-allowed !important;
  transform: none !important;
}

/* Alertas */
.alert-danger {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.3) !important;
  color: #dc2626 !important;
  border-radius: 8px !important;
  font-size: 0.875rem !important;
}

.dark-theme .alert-danger {
  background: rgba(239, 68, 68, 0.15) !important;
  border-color: rgba(239, 68, 68, 0.4) !important;
  color: #fca5a5 !important;
}

/* Spinner personalizado */
.spinner-border-sm {
  width: 1rem !important;
  height: 1rem !important;
}

/* Animaciones suaves */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Responsive */
@media (max-width: 768px) {
  .login-card {
    padding: 2rem 1.5rem;
    margin: 1rem;
  }

  .login-title {
    font-size: 1.75rem;
  }

  .theme-toggle {
    top: 15px;
    right: 15px;
  }

  .theme-toggle-btn {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
}

/* Mejoras de accesibilidad */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus visible para navegación por teclado */
.theme-toggle-btn:focus-visible,
.login-btn:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}
</style>
